import React, { useState, useEffect, useRef } from 'react';
import '../styles/HowItWorks.css';

const HowItWorks: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);

  const tabs = [
    {
      title: "Start a Supplier Search",
      description: "Tell the AI agent exactly what you need, and it instantly begins finding the best-matching suppliers for your requirements.",
      image: "/media/mac_image_one.png"
    },
    {
      title: "Track Quotation Progress", 
      description: "See a live overview of every supplier contacted, the status of each request, and how many have responded with quotes.",
      image: "/media/mac_image_two.png"
    },
    {
      title: "Quote Comparison Table",
      description: "Side-by-side quote comparisons highlighting price, lead time, and key terms for faster decisions.",
      image: "/media/mac_image_three.png"
    },
    {
      title: "Savings Dashboard / Analytics View",
      description: "Real-time tracking of identified and realized savings across all sourcing activities.",
      image: "/media/mac_image_four.png"
    }
  ];

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleTabChange = (index: number) => {
    if (index === activeTab || isTransitioning) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTab(index);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
    }, 200);
  };

  const handlePrevTab = () => {
    const newIndex = activeTab > 0 ? activeTab - 1 : tabs.length - 1;
    handleTabChange(newIndex);
  };

  const handleNextTab = () => {
    const newIndex = activeTab < tabs.length - 1 ? activeTab + 1 : 0;
    handleTabChange(newIndex);
  };

  // Touch/swipe handling
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextTab();
    }
    if (isRightSwipe) {
      handlePrevTab();
    }
  };

  return (
    <section className="how-it-works">
      <div className="container">
        <h2 className="section-title">How GordianRFQ Works</h2>
        
        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs-header">
            {tabs.map((tab, index) => (
              <button
                key={index}
                className={`tab-button ${activeTab === index ? 'active' : ''}`}
                onClick={() => handleTabChange(index)}
              >
                {tab.title}
              </button>
            ))}
          </div>
          
          {/* Mobile Navigation Arrows */}
          {isMobile && (
            <div className="mobile-nav-arrows">
              <button className="nav-arrow prev-arrow" onClick={handlePrevTab}>
                ‹
              </button>
              <span className="tab-indicator">
                {activeTab + 1} / {tabs.length}
              </span>
              <button className="nav-arrow next-arrow" onClick={handleNextTab}>
                ›
              </button>
            </div>
          )}
          
          {/* Tab Content */}
          <div 
            className={`tab-content ${isTransitioning ? 'transitioning' : ''}`}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div className="content-left">
              <h3 className={`content-title ${isTransitioning ? 'fade-out' : 'fade-in'}`}>
                {tabs[activeTab].title}
              </h3>
              <p className={`content-description ${isTransitioning ? 'fade-out' : 'fade-in'}`}>
                {tabs[activeTab].description}
              </p>
            </div>
            <div className="content-right">
              <div className="snapshot-container">
                <img 
                  src={tabs[activeTab].image} 
                  alt={tabs[activeTab].title}
                  className={`snapshot-image ${isTransitioning ? 'fade-out' : 'fade-in'}`}
                  onClick={() => setIsImageModalOpen(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      {isImageModalOpen && (
        <div className="image-modal-overlay" onClick={() => setIsImageModalOpen(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close-btn"
              onClick={() => setIsImageModalOpen(false)}
            >
              ×
            </button>
            <img 
              src={tabs[activeTab].image} 
              alt={tabs[activeTab].title}
              className="modal-image"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default HowItWorks;
